/**
 * All scene lighting: ambient, directional (key/fill/rim/bottom-fill),
 * HDR environment map, animated moving light, and shadow ground plane.
 *
 * Extracted from ThreeScene so lighting concerns are isolated from
 * model rendering and post-processing.
 */

import { useRef, useMemo, useState, useEffect } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import { Environment } from '@react-three/drei';
import * as THREE from 'three';
import type { SceneSettings } from '@/contexts/SceneSettingsContext';
import type {
  SceneLight,
  LightOrbit,
  LightLFO,
  LightColorLFO,
  LightLFOBindings,
} from '@/worker/types';
import type { ProjectFile } from '@/components/files/FileManager';
import { rasterizeBitmap } from '@/components/viewer/materials/useTextureScripts';

// ---------------------------------------------------------------------------
// Module-level constants
// ---------------------------------------------------------------------------

const EMPTY_LIGHTS: SceneLight[] = [];

const EMPTY_FILES: ProjectFile[] = [];

/** Default raster size for SVG gobos when no explicit dimensions are given. */
const GOBO_DEFAULT_SIZE = 512;

// ---------------------------------------------------------------------------
// useSpotGoboMap — loads a SVG/PNG project file as a Three.js gobo texture
// ---------------------------------------------------------------------------

/**
 * Loads a named project file (SVG or PNG) as a THREE.CanvasTexture suitable
 * for use as a SpotLight.map (gobo / light projection mask).
 *
 * Returns `null` until the texture is ready, then the live texture.
 * Disposes the texture automatically when the filename changes or on unmount.
 */
function useSpotGoboMap(
  filename: string | undefined,
  projectFiles: ProjectFile[],
): THREE.CanvasTexture | null {
  const [texture, setTexture] = useState<THREE.CanvasTexture | null>(null);

  useEffect(() => {
    if (!filename) {
      setTexture(null);
      return;
    }

    const file = projectFiles.find((f) => f.filename === filename);
    if (!file) {
      console.warn(`[SpotGoboMap] File not found in project: "${filename}"`);
      setTexture(null);
      return;
    }

    let cancelled = false;
    let createdTexture: THREE.CanvasTexture | null = null;

    (async () => {
      try {
        const bitmap = await rasterizeBitmap(
          file.content,
          filename,
          GOBO_DEFAULT_SIZE,
          GOBO_DEFAULT_SIZE,
        );
        if (cancelled) {
          bitmap.close();
          return;
        }
        const canvas = new OffscreenCanvas(bitmap.width, bitmap.height);
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          bitmap.close();
          return;
        }
        ctx.drawImage(bitmap, 0, 0);
        bitmap.close();

        const tex = new THREE.CanvasTexture(canvas as unknown as HTMLCanvasElement);
        tex.colorSpace = THREE.SRGBColorSpace;
        tex.needsUpdate = true;
        createdTexture = tex;
        setTexture(tex);
      } catch (err) {
        console.error(`[SpotGoboMap] Failed to load gobo map "${filename}":`, err);
      }
    })();

    return () => {
      cancelled = true;
      // Dispose on cleanup (next filename or unmount)
      if (createdTexture) {
        createdTexture.dispose();
        createdTexture = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filename, projectFiles]);

  return texture;
}

// ---------------------------------------------------------------------------
// Orbit math helpers (exported for use in wireframe visualization)
// ---------------------------------------------------------------------------

/**
 * Build the right/up basis vectors for an orbit plane from Euler tilt angles
 * (degrees, intrinsic XYZ), optionally precessed by `driftAngleDeg` degrees
 * around `driftAxis`.
 */
export function buildOrbitFrame(
  tilt: [number, number, number],
  driftAngleDeg: number,
  driftAxis: [number, number, number],
): { right: THREE.Vector3; up: THREE.Vector3 } {
  // Start with the base Euler rotation to orient the orbit plane
  const euler = new THREE.Euler(
    THREE.MathUtils.degToRad(tilt[0]),
    THREE.MathUtils.degToRad(tilt[1]),
    THREE.MathUtils.degToRad(tilt[2]),
    'XYZ',
  );
  const baseMatrix = new THREE.Matrix4().makeRotationFromEuler(euler);

  // Apply drift rotation around the drift axis
  if (driftAngleDeg !== 0) {
    const driftAxisVec = new THREE.Vector3(
      driftAxis[0],
      driftAxis[1],
      driftAxis[2],
    ).normalize();
    const driftQuat = new THREE.Quaternion().setFromAxisAngle(
      driftAxisVec,
      THREE.MathUtils.degToRad(driftAngleDeg),
    );
    const driftMatrix = new THREE.Matrix4().makeRotationFromQuaternion(
      driftQuat,
    );
    baseMatrix.premultiply(driftMatrix);
  }

  // Extract right (X column) and up (Y column) of the rotated frame
  const right = new THREE.Vector3().setFromMatrixColumn(baseMatrix, 0);
  const up = new THREE.Vector3().setFromMatrixColumn(baseMatrix, 1);
  return { right, up };
}

/**
 * Compute the world position of an orbiting light at a given angle (radians).
 *
 * The anchor point (`position`) sits at angle 0 on the orbit. The orbit
 * center is displaced from the anchor along the -right direction by `radius`,
 * so the anchor remains exactly on the ellipse.
 */
export function computeOrbitPosition(
  anchor: [number, number, number],
  orbit: LightOrbit,
  angleDeg: number,
  driftAngleDeg: number,
  out: THREE.Vector3,
): void {
  const { right, up } = buildOrbitFrame(
    orbit.tilt,
    driftAngleDeg,
    orbit.driftAxis,
  );
  const r = orbit.radius;
  const rB = orbit.radiusB > 0 ? orbit.radiusB : r;

  // Orbit center = anchor - right * r  (so anchor is at angle=0)
  const center = new THREE.Vector3(
    anchor[0],
    anchor[1],
    anchor[2],
  ).addScaledVector(right, -r);

  const rad = THREE.MathUtils.degToRad(angleDeg);
  out
    .copy(center)
    .addScaledVector(right, r * Math.cos(rad))
    .addScaledVector(up, rB * Math.sin(rad));
}

// ---------------------------------------------------------------------------
// LFO evaluation helpers
// ---------------------------------------------------------------------------

/**
 * Evaluate a single-channel LFO at the given elapsed time (seconds).
 *
 * The cycle position `t` is computed from time, frequency, and phase offset.
 * The output is mapped to [min, max] with an absolute scale (independent of
 * the light's base value).
 *
 * Waveforms:
 *  - sine:     smooth 0→1→0, starts at 0 (cosine-based)
 *  - triangle: linear 0→1→0 per cycle
 *  - square:   0 for first half-cycle, 1 for second half
 *  - sawtooth: linear ramp 0→1, then instant reset
 */
export function evaluateLFO(lfo: LightLFO, time: number): number {
  // Normalized cycle position 0..1, accounting for freq + phase offset
  const t = (((time * lfo.frequency) % 1) + lfo.phase / 360 + 1) % 1;
  let v: number;
  switch (lfo.shape) {
    case 'sine':
      // 0.5 - 0.5*cos(2πt): starts at 0, peaks at 1 at t=0.5, returns to 0
      v = 0.5 - 0.5 * Math.cos(t * 2 * Math.PI);
      break;
    case 'triangle':
      v = t < 0.5 ? 2 * t : 2 - 2 * t;
      break;
    case 'square':
      // 0 for first half, 1 for second half
      // (phase=0 → off at t=0; phase=180 → on at t=0, giving alternating strobes)
      v = t < 0.5 ? 0 : 1;
      break;
    case 'sawtooth':
      v = t;
      break;
    default:
      v = 0;
  }
  return lfo.min + v * (lfo.max - lfo.min);
}

/**
 * Evaluate a whole-color LFO at the given elapsed time (seconds).
 * The LFO waveform (0→1) is used to lerp from colorA to colorB.
 */
export function evaluateColorLFO(
  lfo: LightColorLFO,
  time: number,
): [number, number, number] {
  // Re-use the sine formula from evaluateLFO with a temporary LightLFO-like object
  const t = (((time * lfo.frequency) % 1) + lfo.phase / 360 + 1) % 1;
  let v: number;
  switch (lfo.shape) {
    case 'sine':
      v = 0.5 - 0.5 * Math.cos(t * 2 * Math.PI);
      break;
    case 'triangle':
      v = t < 0.5 ? 2 * t : 2 - 2 * t;
      break;
    case 'square':
      v = t < 0.5 ? 0 : 1;
      break;
    case 'sawtooth':
      v = t;
      break;
    default:
      v = 0;
  }
  return [
    lfo.colorA[0] + v * (lfo.colorB[0] - lfo.colorA[0]),
    lfo.colorA[1] + v * (lfo.colorB[1] - lfo.colorA[1]),
    lfo.colorA[2] + v * (lfo.colorB[2] - lfo.colorA[2]),
  ];
}

/**
 * Apply LFO bindings to a Three.js light ref each frame.
 * Mutates the light's intensity and/or color in place.
 *
 * @param lightRef  - Three.js light object
 * @param lfo       - LFO bindings parsed from SCAD
 * @param baseColor - The static color from SCAD ([r, g, b] in 0..1)
 * @param baseIntensity - The static intensity from SCAD (pre-scaling)
 * @param intensityScale - Scaling factor applied to intensity (10 or 100)
 * @param time      - Elapsed time in seconds
 */
function applyLFOBindings(
  lightRef: THREE.Light,
  lfo: LightLFOBindings,
  baseColor: [number, number, number],
  baseIntensity: number,
  intensityScale: number,
  time: number,
): void {
  // Intensity
  if (lfo.intensity) {
    lightRef.intensity = evaluateLFO(lfo.intensity, time) * intensityScale;
  }

  // Color — whole-color LFO takes precedence over per-channel
  if (lfo.color) {
    const [r, g, b] = evaluateColorLFO(lfo.color, time);
    lightRef.color.setRGB(r, g, b);
  } else {
    const r = lfo.colorR ? evaluateLFO(lfo.colorR, time) : baseColor[0];
    const g = lfo.colorG ? evaluateLFO(lfo.colorG, time) : baseColor[1];
    const b = lfo.colorB ? evaluateLFO(lfo.colorB, time) : baseColor[2];
    if (lfo.colorR || lfo.colorG || lfo.colorB) {
      lightRef.color.setRGB(r, g, b);
    }
  }
}

/**
 * Apply spot-specific LFO bindings (angle, penumbra, and gobo map rotation).
 */
function applySpotLFOBindings(
  spotLight: THREE.SpotLight,
  lfo: LightLFOBindings,
  baseAngle: number,
  basePenumbra: number,
  time: number,
): void {
  if (lfo.angle) {
    spotLight.angle = THREE.MathUtils.degToRad(evaluateLFO(lfo.angle, time));
  }
  if (lfo.penumbra) {
    spotLight.penumbra = evaluateLFO(lfo.penumbra, time);
  }
  if (lfo.rotation && spotLight.map) {
    // Ensure rotation pivots around the texture centre
    spotLight.map.center.set(0.5, 0.5);
    spotLight.map.rotation = THREE.MathUtils.degToRad(
      evaluateLFO(lfo.rotation, time),
    );
  }
  // Suppress unused-variable warnings for base values when not used
  void baseAngle;
  void basePenumbra;
}

// ---------------------------------------------------------------------------
// AnimatedOrbitLight — wraps a custom light with a user-defined orbit
// ---------------------------------------------------------------------------

/**
 * Shared ref type so the wireframe can read the animated position.
 * Exposed as a THREE.Vector3 updated every frame.
 */
export type OrbitPositionRef = React.RefObject<THREE.Vector3>;

function AnimatedOrbitLight({
  light,
  positionRef,
  castShadow,
  sceneSettings,
  paused = false,
  projectFiles = EMPTY_FILES,
}: {
  light: Exclude<SceneLight, { type: 'ambient' }> & { orbit: LightOrbit };
  positionRef?: OrbitPositionRef;
  castShadow: boolean;
  sceneSettings: SceneSettings;
  paused?: boolean;
  projectFiles?: ProjectFile[];
}) {
  const lightRef = useRef<
    THREE.DirectionalLight | THREE.PointLight | THREE.SpotLight
  >(null);
  const { invalidate } = useThree();
  // When paused, we freeze the elapsed time so the position stays fixed.
  const frozenTime = useRef<number | null>(null);

  const color = useMemo(
    () =>
      new THREE.Color(light.color[0], light.color[1], light.color[2]).getHex(),
    [light.color],
  );

  const anchor = light.position;
  const orbit = light.orbit;
  const lfo = light.lfo;
  // Intensity scaling factor (same as static lights below)
  const intensityScale = light.type === 'directional' ? 10 : 100;

  // Gobo map for spot lights
  const mapFilename = light.type === 'spot' ? light.map : undefined;
  const goboMap = useSpotGoboMap(mapFilename, projectFiles);

  // Apply gobo map imperatively whenever it changes
  useEffect(() => {
    if (light.type !== 'spot' || !(lightRef.current instanceof THREE.SpotLight)) return;
    lightRef.current.map = goboMap;
    if (goboMap) goboMap.center.set(0.5, 0.5);
  }, [goboMap, light.type]);

  useFrame((state) => {
    if (!lightRef.current) return;

    if (paused) {
      // Capture the time at the moment of pausing, then stop updating.
      if (frozenTime.current === null) {
        frozenTime.current = state.clock.getElapsedTime();
      }
      return;
    }
    // Clear frozen time when resuming so the next frame uses live time.
    frozenTime.current = null;

    const time = state.clock.getElapsedTime();
    const angleDeg = time * orbit.speed * 360;
    const driftAngleDeg = time * orbit.driftSpeed;

    const pos = new THREE.Vector3();
    computeOrbitPosition(anchor, orbit, angleDeg, driftAngleDeg, pos);

    lightRef.current.position.copy(pos);

    // Directional and spot lights need to aim at the origin
    if (
      lightRef.current instanceof THREE.DirectionalLight ||
      lightRef.current instanceof THREE.SpotLight
    ) {
      lightRef.current.target.position.set(0, 0, 0);
      lightRef.current.target.updateMatrixWorld();
    }

    // Apply LFO modulation on top of orbit (intensity, color, spot params)
    if (lfo) {
      applyLFOBindings(
        lightRef.current,
        lfo,
        light.color,
        light.intensity,
        intensityScale,
        time,
      );
      if (
        light.type === 'spot' &&
        lightRef.current instanceof THREE.SpotLight
      ) {
        applySpotLFOBindings(
          lightRef.current,
          lfo,
          light.angle,
          light.penumbra,
          time,
        );
      }
    }

    // Share position with wireframe
    if (positionRef?.current) {
      positionRef.current.copy(pos);
    }

    invalidate();
  });

  if (light.type === 'directional') {
    return (
      <directionalLight
        ref={lightRef as React.RefObject<THREE.DirectionalLight>}
        color={color}
        intensity={light.intensity * 10}
        castShadow={castShadow}
        shadow-mapSize-width={1024}
        shadow-mapSize-height={1024}
      />
    );
  }
  if (light.type === 'point') {
    return (
      <pointLight
        ref={lightRef as React.RefObject<THREE.PointLight>}
        color={color}
        intensity={light.intensity * 100}
        distance={light.distance}
        decay={light.decay}
        castShadow={castShadow}
      />
    );
  }
  if (light.type === 'spot') {
    return (
      <spotLight
        ref={lightRef as React.RefObject<THREE.SpotLight>}
        color={color}
        intensity={light.intensity * 100}
        distance={light.distance}
        angle={light.angle}
        penumbra={light.penumbra}
        decay={light.decay}
        castShadow={castShadow}
        shadow-mapSize-width={1024}
        shadow-mapSize-height={1024}
      />
    );
  }
  return null;
}

// ---------------------------------------------------------------------------
// AnimatedLFOLight — wraps a custom light with LFO bindings (no orbit)
// ---------------------------------------------------------------------------

/**
 * Renders a static-position light whose intensity and/or color are modulated
 * each frame by LFO bindings. Used when a light has `lfo` but no `orbit`.
 *
 * For lights that have both orbit and LFOs, AnimatedOrbitLight handles both.
 */
function AnimatedLFOLight({
  light,
  castShadow,
  paused = false,
  projectFiles = EMPTY_FILES,
}: {
  light: SceneLight & { lfo: LightLFOBindings };
  castShadow: boolean;
  paused?: boolean;
  projectFiles?: ProjectFile[];
}) {
  const lightRef = useRef<
    | THREE.AmbientLight
    | THREE.DirectionalLight
    | THREE.PointLight
    | THREE.SpotLight
  >(null);
  const { invalidate } = useThree();
  const frozenTime = useRef<number | null>(null);

  const lfo = light.lfo;
  // Intensity scaling matches static light rendering below
  const intensityScale =
    light.type === 'ambient' || light.type === 'directional' ? 10 : 100;

  // Gobo map for spot lights
  const mapFilename = light.type === 'spot' ? light.map : undefined;
  const goboMap = useSpotGoboMap(mapFilename, projectFiles);

  // Apply gobo map imperatively whenever it changes
  useEffect(() => {
    if (light.type !== 'spot' || !(lightRef.current instanceof THREE.SpotLight)) return;
    lightRef.current.map = goboMap;
    if (goboMap) goboMap.center.set(0.5, 0.5);
  }, [goboMap, light.type]);

  // Base values for static parts of the light
  const baseIntensity = light.intensity * intensityScale;
  const baseColor = useMemo(
    () =>
      new THREE.Color(light.color[0], light.color[1], light.color[2]).getHex(),
    [light.color],
  );

  useFrame((state) => {
    if (!lightRef.current) return;

    if (paused) {
      if (frozenTime.current === null) {
        frozenTime.current = state.clock.getElapsedTime();
      }
      return;
    }
    frozenTime.current = null;

    const time = state.clock.getElapsedTime();

    applyLFOBindings(
      lightRef.current,
      lfo,
      light.color,
      light.intensity,
      intensityScale,
      time,
    );

    if (light.type === 'spot' && lightRef.current instanceof THREE.SpotLight) {
      applySpotLFOBindings(
        lightRef.current,
        lfo,
        light.angle,
        light.penumbra,
        time,
      );
    }

    invalidate();
  });

  if (light.type === 'ambient') {
    return (
      <ambientLight
        ref={lightRef as React.RefObject<THREE.AmbientLight>}
        color={baseColor}
        intensity={baseIntensity}
      />
    );
  }
  if (light.type === 'directional') {
    return (
      <directionalLight
        ref={lightRef as React.RefObject<THREE.DirectionalLight>}
        position={
          new THREE.Vector3(
            light.position[0],
            light.position[1],
            light.position[2],
          )
        }
        color={baseColor}
        intensity={baseIntensity}
        castShadow={castShadow}
        shadow-mapSize-width={1024}
        shadow-mapSize-height={1024}
      />
    );
  }
  if (light.type === 'point') {
    return (
      <pointLight
        ref={lightRef as React.RefObject<THREE.PointLight>}
        position={
          new THREE.Vector3(
            light.position[0],
            light.position[1],
            light.position[2],
          )
        }
        color={baseColor}
        intensity={baseIntensity}
        distance={light.distance}
        decay={light.decay}
        castShadow={castShadow}
      />
    );
  }
  if (light.type === 'spot') {
    return (
      <spotLight
        ref={lightRef as React.RefObject<THREE.SpotLight>}
        position={
          new THREE.Vector3(
            light.position[0],
            light.position[1],
            light.position[2],
          )
        }
        color={baseColor}
        intensity={baseIntensity}
        distance={light.distance}
        angle={light.angle}
        penumbra={light.penumbra}
        decay={light.decay}
        castShadow={castShadow}
        shadow-mapSize-width={1024}
        shadow-mapSize-height={1024}
      />
    );
  }
  return null;
}

// ---------------------------------------------------------------------------
// MovingLight — animated orbiting directional light
// ---------------------------------------------------------------------------

function MovingLight({
  active,
  radius = 40,
  height = 25,
  speed = 0.5,
}: {
  active: boolean;
  radius?: number;
  height?: number;
  speed?: number;
}) {
  const groupRef = useRef<THREE.Group>(null);
  const dirLightRef = useRef<THREE.DirectionalLight>(null);
  const pointLightRef = useRef<THREE.PointLight>(null);
  const meshRef = useRef<THREE.Mesh>(null);
  // Track whether the animation is still transitioning so we can stop
  // invalidating frames once the light has fully faded out.
  const isAnimating = useRef(active);
  const { invalidate } = useThree();

  useFrame((state, delta) => {
    // Skip if neither active nor still fading out
    if (!active && !isAnimating.current) return;

    const time = state.clock.getElapsedTime();
    const x = Math.cos(time * speed) * radius;
    const z = Math.sin(time * speed) * radius;
    const y = height + Math.sin(time * speed * 2) * 8;

    if (groupRef.current) {
      groupRef.current.position.set(x, y, z);
      groupRef.current.lookAt(0, 0, 0);
    }

    // Smoothly transition intensity and scale
    const lerpFactor = Math.min(delta * 4, 1); // Adjust speed of transition

    if (dirLightRef.current) {
      dirLightRef.current.intensity = THREE.MathUtils.lerp(
        dirLightRef.current.intensity,
        active ? 1.5 : 0,
        lerpFactor,
      );
    }

    if (pointLightRef.current) {
      pointLightRef.current.intensity = THREE.MathUtils.lerp(
        pointLightRef.current.intensity,
        active ? 0.5 : 0,
        lerpFactor,
      );
    }

    let stillMoving = active;
    if (meshRef.current) {
      const targetScale = active ? 1 : 0;
      const currentScale = meshRef.current.scale.x;
      const newScale = THREE.MathUtils.lerp(
        currentScale,
        targetScale,
        lerpFactor,
      );

      meshRef.current.scale.setScalar(newScale);
      meshRef.current.visible = newScale > 0.01;
      // Still transitioning if we haven't reached the target
      if (Math.abs(newScale - targetScale) > 0.001) stillMoving = true;
    }

    isAnimating.current = stillMoving;

    // Drive the render loop while the animation is running
    if (stillMoving) invalidate();
  });

  return (
    <group ref={groupRef}>
      <directionalLight
        ref={dirLightRef}
        intensity={0} // Start at 0, animate up
        color="#fff8e7"
        castShadow
        shadow-mapSize-width={2048}
        shadow-mapSize-height={2048}
        shadow-camera-far={150}
        shadow-camera-left={-50}
        shadow-camera-right={50}
        shadow-camera-top={50}
        shadow-camera-bottom={-50}
        shadow-bias={-0.0001}
      />
      <mesh ref={meshRef} visible={false}>
        <sphereGeometry args={[1.5, 16, 16]} />
        <meshBasicMaterial color="#ffdd88" />
      </mesh>
      <pointLight
        ref={pointLightRef}
        intensity={0}
        color="#ffdd88"
        distance={10}
        decay={2}
      />
    </group>
  );
}

// ---------------------------------------------------------------------------
// FadingShadowPlane — animated ground plane
// ---------------------------------------------------------------------------

function FadingShadowPlane({
  active,
  opacity,
}: {
  active: boolean;
  opacity: number;
}) {
  const materialRef = useRef<THREE.ShadowMaterial>(null);
  const isAnimating = useRef(active);
  const { invalidate } = useThree();

  useFrame((_, delta) => {
    if (!active && !isAnimating.current) return;
    if (!materialRef.current) return;

    const targetOpacity = active ? opacity : 0;
    const lerpFactor = Math.min(delta * 4, 1);

    materialRef.current.opacity = THREE.MathUtils.lerp(
      materialRef.current.opacity,
      targetOpacity,
      lerpFactor,
    );

    const stillTransitioning =
      Math.abs(materialRef.current.opacity - targetOpacity) > 0.001;
    isAnimating.current = stillTransitioning || active;

    if (isAnimating.current) invalidate();
  });

  return (
    <mesh
      rotation={[-Math.PI / 2, 0, 0]}
      position={[0, -0.05, 0]}
      receiveShadow
    >
      <planeGeometry args={[200, 200]} />
      <shadowMaterial
        ref={materialRef}
        transparent
        opacity={0} // Start invisible
      />
    </mesh>
  );
}

// ---------------------------------------------------------------------------
// StaticSpotLight — spot light with optional gobo map, no animation
// ---------------------------------------------------------------------------

/**
 * Renders a static (non-orbiting, non-LFO) spot light.
 * Extracted into its own component so it can use hooks to manage the gobo map.
 */
function StaticSpotLight({
  light,
  lightKey,
  castShadow,
  projectFiles = EMPTY_FILES,
}: {
  light: import('@/worker/types').LightSpot;
  lightKey: string;
  castShadow: boolean;
  projectFiles?: ProjectFile[];
}) {
  const lightRef = useRef<THREE.SpotLight>(null);
  const color = useMemo(
    () =>
      new THREE.Color(light.color[0], light.color[1], light.color[2]).getHex(),
    [light.color],
  );
  const goboMap = useSpotGoboMap(light.map, projectFiles);

  useEffect(() => {
    if (!lightRef.current) return;
    lightRef.current.map = goboMap;
    if (goboMap) goboMap.center.set(0.5, 0.5);
  }, [goboMap]);

  return (
    <spotLight
      ref={lightRef}
      key={lightKey}
      position={
        new THREE.Vector3(
          light.position[0],
          light.position[1],
          light.position[2],
        )
      }
      color={color}
      intensity={light.intensity * 100}
      distance={light.distance}
      angle={light.angle}
      penumbra={light.penumbra}
      decay={light.decay}
      castShadow={castShadow}
      shadow-mapSize-width={1024}
      shadow-mapSize-height={1024}
    />
  );
}

// ---------------------------------------------------------------------------
// SceneLighting — public component
// ---------------------------------------------------------------------------

export function SceneLighting({
  sceneSettings,
  showMovingLight,
  showGrid,
  wireframeOpacity,
  lights = EMPTY_LIGHTS,
  skipEnvironment = false,
  lightAnimationsPaused = false,
  projectFiles = EMPTY_FILES,
}: {
  sceneSettings: SceneSettings;
  showMovingLight: boolean;
  showGrid: boolean;
  wireframeOpacity: number;
  lights?: SceneLight[];
  /**
   * When true, skip loading the HDR environment map. Used for blog embeds
   * where multiple canvases share a page: each canvas independently allocates
   * a PMREMGenerator render target for the env map, which can exhaust GPU
   * memory. The envMapIntensity is very low (0.1) so the visual impact is
   * negligible.
   */
  skipEnvironment?: boolean;
  /** When true, all light orbit and LFO animations are frozen at their current state. */
  lightAnimationsPaused?: boolean;
  /** Project files used to resolve gobo map filenames in light_spot(map=…). */
  projectFiles?: ProjectFile[];
}) {
  // When wireframe is fully opaque, skip all lighting (nothing solid to light)
  if (wireframeOpacity >= 1) return null;

  return (
    <>
      {/* Environment map — provides reflections and ambient IBL.
          Skipped in product/blog mode to save GPU memory per canvas. */}
      {!skipEnvironment && (
        <Environment files={`${import.meta.env.BASE_URL}/city.hdr`} />
      )}

      {/* Custom Scene Lights */}
      {lights.map((light, index) => {
        const lightKey =
          'position' in light
            ? `${light.type}-${light.position.join(',')}`
            : `${light.type}-${index}`;
        const color = new THREE.Color(
          light.color[0],
          light.color[1],
          light.color[2],
        ).getHex();

        // Lights with an orbit are rendered by AnimatedOrbitLight which
        // drives position each frame via useFrame. Ambient lights never have
        // a position so they can never have an orbit.
        if ('orbit' in light && light.orbit) {
          return (
            <AnimatedOrbitLight
              key={lightKey}
              light={
                light as Exclude<SceneLight, { type: 'ambient' }> & {
                  orbit: NonNullable<typeof light.orbit>;
                }
              }
              castShadow={sceneSettings.shadow.enabled}
              sceneSettings={sceneSettings}
              paused={lightAnimationsPaused}
              projectFiles={projectFiles}
            />
          );
        }

        // Lights with LFO bindings (but no orbit) are rendered by AnimatedLFOLight
        // which modulates intensity/color/spot-params each frame via useFrame.
        if (light.lfo) {
          return (
            <AnimatedLFOLight
              key={lightKey}
              light={
                light as SceneLight & { lfo: NonNullable<typeof light.lfo> }
              }
              castShadow={sceneSettings.shadow.enabled}
              paused={lightAnimationsPaused}
              projectFiles={projectFiles}
            />
          );
        }

        switch (light.type) {
          case 'ambient':
            return (
              <ambientLight
                key={lightKey}
                color={color}
                intensity={light.intensity * 10}
              />
            );
          case 'directional':
            return (
              <directionalLight
                key={lightKey}
                position={
                  new THREE.Vector3(
                    light.position[0],
                    light.position[1],
                    light.position[2],
                  )
                }
                color={color}
                intensity={light.intensity * 10}
                castShadow={sceneSettings.shadow.enabled}
                shadow-mapSize-width={1024}
                shadow-mapSize-height={1024}
              />
            );
          case 'point':
            return (
              <pointLight
                key={lightKey}
                position={
                  new THREE.Vector3(
                    light.position[0],
                    light.position[1],
                    light.position[2],
                  )
                }
                color={color}
                intensity={light.intensity * 100}
                distance={light.distance}
                decay={light.decay}
                castShadow={sceneSettings.shadow.enabled}
              />
            );
          case 'spot':
            return (
              <StaticSpotLight
                key={lightKey}
                lightKey={lightKey}
                light={light}
                castShadow={sceneSettings.shadow.enabled}
                projectFiles={projectFiles}
              />
            );
          default:
            return null;
        }
      })}

      {/* Soft ambient */}
      <ambientLight intensity={sceneSettings.light.ambientIntensity} />

      {/* Static directional lights */}
      <>
        {/* Key light — strongest, casts shadows */}
        <directionalLight
          position={[10, 15, 10]}
          intensity={sceneSettings.light.keyLightIntensity}
          castShadow={sceneSettings.shadow.enabled}
          shadow-mapSize-width={sceneSettings.shadow.mapSize}
          shadow-mapSize-height={sceneSettings.shadow.mapSize}
          shadow-camera-far={100}
          shadow-camera-left={-50}
          shadow-camera-right={50}
          shadow-camera-top={50}
          shadow-camera-bottom={-50}
          shadow-bias={-0.0001}
        />
        {/* Fill light — softer, opposite side */}
        <directionalLight
          position={[-8, 8, -5]}
          intensity={sceneSettings.light.fillLightIntensity}
        />
        {/* Rim/back light — depth separation */}
        <directionalLight
          position={[0, 5, -15]}
          intensity={sceneSettings.light.rimLightIntensity}
        />
        {/* Bottom fill — reduce harsh under-shadows */}
        <directionalLight
          position={[0, -5, 0]}
          intensity={sceneSettings.light.rimLightIntensity * 0.5}
        />
      </>

      {/* Animated orbiting light — always rendered, fades in/out */}
      <MovingLight active={showMovingLight} />

      {/* Shadow-receiving ground plane — only with grid + shadows, fades in/out with moving light */}
      {showGrid && sceneSettings.shadow.enabled && (
        <FadingShadowPlane
          active={showMovingLight}
          opacity={sceneSettings.shadow.intensity}
        />
      )}
    </>
  );
}
